/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, Fragment } from 'react';
import { PrimaryButton, Modal, TextField, Persona, PersonaSize, DefaultButton, CompoundButton, Spinner, SpinnerSize, Dropdown } from '@fluentui/react';
import { useSelector } from 'react-redux';
import Countdown from 'react-countdown';
import { AUTH_CONFIG } from '../../Auth/auth0-variables';
import Lottie from 'lottie-react-web'
import animation from './Lottie.json'
import animation2 from './LottieClockOut.json'
import axios from 'axios';
import moment from 'moment'
import { Clock20Regular } from '@fluentui/react-icons';
import { subMinutes, differenceInMinutes, format } from 'date-fns'
import differenceInSeconds from 'date-fns/differenceInSeconds'
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import htmr from 'htmr';
import Geocode from 'react-geocode';

// Reverse-geocode the user's current browser location into a readable address.
// Always resolves (never rejects) so it can't block clock in/out.
const getClockLocationAddress = () => {
    // Hard cap the whole operation. The geolocation prompt has its own timeout,
    // but the Geocode network call does not — if it hangs we must NOT block the
    // clock in/out POST, so we race the lookup against a timeout and resolve null.
    const lookup = new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                try {
                    Geocode.setApiKey('AIzaSyCHFO0sCfblfRlaNvvzOP2OaDuZXsLzH4U');
                    Geocode.setLanguage('en');
                    Geocode.fromLatLng(position.coords.latitude, position.coords.longitude).then(
                        (response) => resolve(response?.results?.[0]?.formatted_address || null),
                        () => resolve(null),
                    );
                } catch {
                    resolve(null);
                }
            },
            () => resolve(null),
            { maximumAge: 10000, timeout: 5000, enableHighAccuracy: true },
        );
    });

    const timeout = new Promise((resolve) => {
        setTimeout(() => resolve(null), 8000);
    });

    return Promise.race([lookup, timeout]);
};

const buttonCss = (theme) => css`
        height: 45px;
        width: 280px;
        min-height: 0;
        padding: 0;
        display: flex;
        align-items: center;
        padding-left: 20px;
        border-radius: 4px;
        margin-bottom:15px;
        border:none;
        border: 1px solid rgba(var(--palette-neutral-20, 234, 234, 234), 1);
         &:hover{
                 background-color: var(--fui-white);
            box-shadow: 0 3.2px 7.2px 0 var(--callout-shadow-color, rgba(0, 0, 0, 0.132)), 0 0.6px 1.8px 0 var(--callout-shadow-secondary-color, rgba(0, 0, 0, 0.108));
        }
        &.active{
            background-color: ${theme === 'dark' ? 'rgb(12 44 78)' : 'rgb(226, 239, 253)'};
            border: 1px solid rgb(148, 199, 250);
            color: var(--fui-neutralPrimary);
            :hover{
                background-color: ${theme === 'dark' ? 'rgb(7 32 58)' : 'rgb(204, 228, 252)'};
            }
        }
        .ms-Button-flexContainer{
            justify-content: flex-start;
            align-items: center;
            min-width: 100%;
            height:auto;
            font-size: 15px;
            svg{
                margin-right:10px;
            }
        }
`

const persona = css`
.ms-Persona-details{
    display:none;
}
    .head-xs {
    margin-left: 8px;
    font-size: 16px;
    }
    .ms-Persona-initials {
    font-weight: 400;
    font-size: 14px;
}
`

function ClockinForm(props) {
    const [step, setStep] = useState("personal");
    const [clockedIn, setClockedIn] = useState(false)
    const [loading, setLoading] = useState(true)
    const [clockedInData, setClockedInData] = useState({})
    const [onBreak, setOnBreak] = useState(false)
    const [breakStart, setBreakStart] = useState()
    // CHANGED: breakDuration is a string (elapsed time), default 00:00
    const [breakDuration, setBreakDuration] = useState('00:00')
    const [chooseBreakType, setChooseBreakType] = useState(false);
    const [availableShifts, setAvailableShifts] = useState([]);
    const [selected, setSelected] = useState({});
    const [lateError, setLateError] = useState(false);
    const [disableClockin, setDisableClockin] = useState(true);
    const [countdownDate, setCountdownDate] = useState();
    const [clientBreakId, setClientBreakId] = useState();
    const [selectFacility, setSelectFacility] = useState(false);
    const [selectRole, setSelectRole] = useState(false);
    const [showAddTips, addShowAddTips] = useState(false);
    const [timeCardId, addTimeCardId] = useState(null);
    const [unscheduledRoleId, setUnscheduledRoleId] = useState();
    const [selectedFacility, setSelectedFacility] = useState(props.facilityId)
    const [localUserFacilities, setLocalUserFacilities] = useState([]);
    const [tips, setTips] = useState(null)
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('Too Early');
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientError, setClientError] = useState(false);
    // Active-shift job code (Start/Change/End Job while clocked in)
    const [showJobPicker, setShowJobPicker] = useState(false);
    const [jobPickerClientId, setJobPickerClientId] = useState(null);

    // In-flight action guard: prevents double-submits (spam clicks) and drives the
    // in-button spinners. `inFlight` holds the name of the running action (or null);
    // `inFlightRef` is the synchronous guard so a handler can't re-run before it returns.
    const [inFlight, setInFlight] = useState(null);
    const inFlightRef = React.useRef(false);
    const beginAction = (name) => {
        if (inFlightRef.current) return false;
        inFlightRef.current = true;
        setInFlight(name);
        return true;
    };
    const endAction = () => {
        inFlightRef.current = false;
        setInFlight(null);
    };
    const jobBusy = inFlight === 'startJob' || inFlight === 'endJob';

    // NEW: interval + mirror ref for onBreak to avoid stale closures
    const intervalRef = React.useRef(null);
    const onBreakRef = React.useRef(false);

    useEffect(() => {
        onBreakRef.current = onBreak;
        if (!onBreak && intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, [onBreak]);

    useEffect(() => {
        if (breakStart) {
            calcBreakTime();
        }
    }, [breakStart]);

    // NEW: helpers for real elapsed timer
    const parseTodayTime = (timeStr) => {
        // Expect "HH:mm:ss" (24h) to avoid AM/PM ambiguity
        return moment(moment().format('YYYY-MM-DD') + "T" + timeStr).toDate();
    };

    const formatElapsed = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
        if (hours > 0) {
            return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        }
        return `${pad(minutes)}:${pad(seconds)}`;
    };

    const startTimer = (startDate) => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        // Set initial tick immediately
        setBreakDuration(formatElapsed(differenceInSeconds(new Date(), startDate)));

        intervalRef.current = window.setInterval(() => {
            if (!onBreakRef.current) {
                if (intervalRef.current) {
                    window.clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }
            const dif = differenceInSeconds(new Date(), startDate);
            setBreakDuration(formatElapsed(dif));
        }, 1000);
    };

    // REPLACED: old calcBreakTime (which built a Date and incremented it)
    const calcBreakTime = () => {
        const start = parseTodayTime(breakStart);
        startTimer(start);
    };

    useEffect(() => {
        const source = axios.get(AUTH_CONFIG.API_URL + '/api/Schedules/GetUserClockedInDesktop?userId=' + props.user.userId).then(response => {
            setClockedInData(response.data)
            let data = response.data;
            if (data && data.endTime == null) {
                setClockedIn(true)
                if (data.onBreak) {
                    setOnBreak(true)
                    // Expect backend to return "HH:mm:ss"; if not, normalize to HH:mm:ss
                    setBreakStart(data.onBreak)
                }
            } else {
                setClockedIn(false)
            }
            setLoading(false)
        })
        return () => {
            // cleanup interval on unmount
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, []);

    const { theme, userFacilities, reduxTimecardClientRoles, reduxClientsModule, reduxRequireJobCode } = useSelector(state => ({
        theme: state.rootReducer.theme,
        userFacilities: state.userProfile.userFacilities,
        reduxTimecardClientRoles: state.appState?.timecardClientRoles || [],
        reduxClientsModule: state.appState?.clientSettings?.clientsModule,
        reduxRequireJobCode: state.appState?.clientSettings?.requireJobCode
    }));

    useEffect(() => {
        if (props.shared) {
            setLocalUserFacilities(props.user.userFacilities);
        } else {
            setLocalUserFacilities(userFacilities);
        }
    }, [userFacilities, props.shared, props.user?.userFacilities]);

    useEffect(() => {
        if (props.data.facilityId) {
            let d = moment().format('YYYY-MM-DD')
            debugger;
            axios.get(AUTH_CONFIG.API_URL + '/api/Schedules/GetClockInShifts?date=' + d + '&UserId=' + props.user.userId + '&facilityId=' + props.data.facilityId).then(response => {

                if (response.data.length > 0) {
                    let startTime = moment(moment(response.data[0].date).format('YYYY-MM-DD') + " " + response.data[0].startTime).toDate();
                    let now = new Date();
                    // Skip late error for on-call shifts
                    let isOnCallShift = response.data[0].isOnCall;
                    if (now > startTime && !isOnCallShift) {
                        // users late
                        // hide count down and show late notice
                        // show clock in button
                        setLateError(true)
                        setDisableClockin(false)
                    } else {
                        // check if client prevents early clockin
                        // yes -> check time to next shift and disable button if not within range
                        // show count down
                        if (props.data.allowUnscheduledClockIns) {
                            setDisableClockin(false)
                        } else if (props.data.preventEarlyClockIn) {
                            const result = differenceInMinutes(startTime, now)
                            if (result > props.data.preventEarlyClockinMin) {
                                const timeUserCanClockIn = subMinutes(startTime, props.data.preventEarlyClockinMin)
                                setCountdownDate(timeUserCanClockIn)
                            } else {
                                setDisableClockin(false)
                            }

                        } else {
                            setDisableClockin(false)
                        }
                    }

                } else {
                    // no shifts today
                    // hide countdown
                    // check if client allows unscheduled clockins
                    // show clockin button if they do
                    if (props.data.allowUnscheduledClockIns) {
                        setDisableClockin(false)
                    }
                }
                debugger;
                setAvailableShifts(response.data)
            })
        }
    }, [props.data.facilityId]);

    useEffect(() => {
        if (!props.clientsModule) {
            setClients([]);
            setSelectedClientId(null);
            setClientError(false);
            return;
        }

        if (props.shared) {
            setClients(props.data.clients || []);
            return;
        }

        setLoadingClients(true);
        axios.get(AUTH_CONFIG.API_URL + '/api/Account/GetTimecardClients?includeInactive=false')
            .then((response) => {
                setClients(response.data || []);
            })
            .finally(() => setLoadingClients(false));
    }, [props.clientsModule, props.shared, props.data.clients]);

    const tryClockIn = () => {
        if (clientSelectionMissing) {
            setClientError(true);
            return;
        }

        if (selected.roleId) {
            clockIn(
                selected.facilityId,
                selected.requiredRoleId ?? selected.roleId,
                selected.schedule_x_ShiftId
            );
        } else {
            let tmpFacility = null;
            let tmpRoleId = null

            if (props.shared) {
                tmpFacility = props.facilityId;
                if (localUserFacilities.filter(x => x.facilityID == props.facilityId)[0].facilityRoles.length > 1) {
                    setSelectRole(true);
                    return;
                } else {
                    tmpRoleId = localUserFacilities.filter(x => x.facilityID == tmpFacility)[0].facilityRoles[0].roleId
                }
            }

            if (!props.shared) {
                if (localUserFacilities.length > 1) {
                    setSelectFacility(true);
                    return;
                } else if (localUserFacilities[0].facilityRoles.length > 1) {
                    setSelectedFacility(localUserFacilities[0].facilityID)
                    setSelectRole(true);
                    return;
                } else {
                    tmpFacility = localUserFacilities[0].facilityID
                    tmpRoleId = localUserFacilities[0].facilityRoles[0].roleId
                }
            }
            clockIn(tmpFacility, tmpRoleId, null)
        }
    }

    const clockIn = async (facilityId, roleId, schedule_x_ShiftId) => {
        debugger;
        let now = new Date();
        if (selected.schedule_x_ShiftId != 1 && props.data.preventEarlyClockIn) {
            const result = differenceInMinutes(new Date(selected.fullStartTime), now)
            if (result > props.data.preventEarlyClockinMin) {
                setAlertTitle('Too Early');
                setAlertMessage('You\'re early for your shift. Please wait until your scheduled time and try again.');
                setShowAlert(true);
                return;
            }
        }

        if (!beginAction('clockIn')) return;

        const clockInAddress = await getClockLocationAddress();

        let model = {
            StartTime: format(moment().toDate(), 'HH:mm:ss'),
            Date: moment().format('MM/DD/YYYY HH:mm:ss'),
            FullStartTime: moment().format('MM/DD/YYYY HH:mm:ss'),
            FacilityId: facilityId,
            RoleId: roleId,
            Schedule_x_ShiftId: schedule_x_ShiftId,
            UserId: props.user.userId,
            TimecardClientId: selectedClientId,
            ClockInAddress: clockInAddress
        }

        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/ClockIn', model)
            .then((response) => {
                setStep("success")
                if (selected.note) {
                    setClockedIn(true)
                    setTimeout(function () {
                        props.close(selected.note);
                    }, 16000)
                } else {
                    setClockedIn(true)
                    setTimeout(function () {
                        props.close(selected.note);
                    }, 6000)
                }
            })
            .catch((error) => {
                const message = error?.response?.data || 'Unable to clock in. Please try again.';
                setAlertTitle('Unable to clock in');
                setAlertMessage(message);
                setShowAlert(true);
            })
            .finally(() => {
                endAction();
            });
    }

    const renderer = ({ hours, minutes, seconds, completed }) => {
        if (completed) {
            // Render a completed state
            setDisableClockin(false)
            setCountdownDate(null)
            return "";
        } else {
            // Render a countdown
            return <span>{hours != 0 && <>{hours} Hr :</>} {minutes} Min : {seconds} Sec</span>;
        }
    };

    const takeABreak = (x) => {
        if (!beginAction('takeABreak')) return;
        let model = {
            StartTime: format(moment().toDate(), 'HH:mm:ss'),
            FullStartDate: moment().format('MM/DD/YYYY HH:mm:ss'),
            FullStartTime: moment().format('MM/DD/YYYY HH:mm:ss'),
            EndTime: null,
            FacilityId: props.facilityId,
            ClientBreakId: clientBreakId,
            UserId: props.user.userId
        }

        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/TakeABreak', model).then(response => {
            // CHANGED: store 24h time and reset counter
            setBreakStart(moment().format("HH:mm:ss"))
            setBreakDuration('00:00')
            setOnBreak(true)
            setTimeout(function () {
                setStep("options")
                props.close()
            }, 3000)
        }).finally(() => {
            endAction();
        })
    }

    const endBreak = () => {
        if (!beginAction('endBreak')) return;
        let model = {
            StartTime: null,
            EndTime: format(moment().toDate(), 'HH:mm:ss'),
            FullEndTimeDate: moment().format('MM/DD/YYYY HH:mm:ss'),
            FullEndTime: moment().format('MM/DD/YYYY HH:mm:ss'),
            FacilityId: props.facilityId,
            UserId: props.user.userId
        }

        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/EndBreak', model).then(response => {
            // CHANGED: stop timer and reset UI
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setOnBreak(false)
            setBreakStart(undefined)
            setBreakDuration('00:00')
        }).finally(() => {
            endAction();
        })
    }

    const clockOut = async () => {
        if (!beginAction('clockOut')) return;
        const clockOutAddress = await getClockLocationAddress();
        let model = {
            EndTime: format(moment().toDate(), 'HH:mm:ss'),
            FullStartDate: moment().format('MM/DD/YYYY HH:mm:ss'),
            FullEndTime: moment().format('MM/DD/YYYY HH:mm:ss'),
            FacilityId: props.facilityId,
            UserId: props.user.userId,
            ClockOutAddress: clockOutAddress
        }
        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/ClockOut', model).then(response => {
            debugger;
            if (response.data.ShowTimeCard) {
                addShowAddTips(true);
                setStep('tips');
                addTimeCardId(response.data.TimeCardId);
            } else {
                showClockoutSuccess();
            }
        }).catch((error) => {
            const message = error?.response?.data || 'Unable to clock out. Please try again.';
            setAlertTitle('Unable to clock out');
            setAlertMessage(message);
            setShowAlert(true);
        }).finally(() => {
            endAction();
        })
    }

    const showClockoutSuccess = () => {
        setClockedIn(false)
        setStep("clockOutSuccess")
        addShowAddTips(false);
        setTimeout(function () {
            setStep("options")
            props.close()
        }, 3000)
    }

    // Re-fetch the clocked-in state (used after Start/Change/End Job).
    const refreshClockedInData = () => {
        return axios.get(AUTH_CONFIG.API_URL + '/api/Schedules/GetUserClockedInDesktop?userId=' + props.user.userId)
            .then(response => {
                setClockedInData(response.data || {});
                return response.data;
            })
            .catch(() => { });
    };

    // Start (or change to) a job code while clocked in. Auto-ends the active job on the server.
    const startJob = async (timecardClientId) => {
        if (!timecardClientId) return;
        if (!beginAction('startJob')) return;
        const startAddress = await getClockLocationAddress();
        const model = {
            UserId: props.user.userId,
            FacilityId: props.facilityId,
            TimecardClientId: timecardClientId,
            StartAddress: startAddress,
            FullStartTime: moment().format('MM/DD/YYYY HH:mm:ss')
        };
        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/StartJob', model)
            .then(() => refreshClockedInData())
            .finally(() => {
                endAction();
                setShowJobPicker(false);
                setJobPickerClientId(null);
            });
    };

    // End the active job (records end time + address).
    const endJob = async () => {
        if (!beginAction('endJob')) return;
        const endAddress = await getClockLocationAddress();
        const model = {
            UserId: props.user.userId,
            FacilityId: props.facilityId,
            EndAddress: endAddress,
            FullEndTime: moment().format('MM/DD/YYYY HH:mm:ss')
        };
        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/EndJob', model)
            .then(() => refreshClockedInData())
            .finally(() => endAction());
    };

    const button = (model, checked) => {
        debugger;
        return <CompoundButton css={buttonCss(theme)} onClick={() => { setSelected(model) }} className={`${checked ? "active" : ""} shift-bg-color ${model.colorId}`} primary={checked}>
            <Clock20Regular />
            {model.fromToFormatted ? model.fromToFormatted : 'Unscheduled Shift'}
        </CompoundButton>
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        debugger;
        if (!beginAction('tips')) return;
        let model = {
            Tips: tips,
            TimeCardId: timeCardId,
            UserId: props.user.userId,
            FacilityId: props.facilityId,
        }
        axios.post(AUTH_CONFIG.API_URL + '/api/Schedules/AddTips', model).then(response => {
            showClockoutSuccess(true);
        }).finally(() => {
            endAction();
        })
        debugger;
    };

    const activeTimecardClientRoles = props.shared ? (props.timecardClientRoles || []) : (reduxTimecardClientRoles || []);
    const clientsModuleEnabled = props.shared ? props.clientsModule === true : reduxClientsModule === true;
    // A job code is only REQUIRED at clock-in when the admin has turned on the
    // "require job code" setting. Otherwise the job code is optional and picked
    // via Start Job after clocking in.
    const requireJobCodeEnabled = props.shared ? props.requireJobCode === true : reduxRequireJobCode === true;
    const clientRequired = clientsModuleEnabled && requireJobCodeEnabled;
    const normalizedClientRoles = (activeTimecardClientRoles || [])
        .map(r => (typeof r === 'string' ? r : (r.name || r.Name || r.label || r.text || '')))
        .map(r => r.trim())
        .filter(r => r);
    const allRolesSelected = normalizedClientRoles.length === 0;
    const roleMatches = (roleName) => {
        if (!roleName) return false;
        return normalizedClientRoles.some((role) => role.toLowerCase() === roleName.toLowerCase());
    };
    const selectedRoleName = selected?.roleName;
    let unscheduledRoleName = null;
    let singleUnscheduledRoleName = null;
    if (selectedFacility) {
        const facility = localUserFacilities.find(x => x.facilityID == selectedFacility);
        if (facility && facility.facilityRoles) {
            const selectedRole = facility.facilityRoles.find(x => x.roleId == unscheduledRoleId);
            unscheduledRoleName = selectedRole?.name;
            if (!unscheduledRoleId && facility.facilityRoles.length === 1) {
                singleUnscheduledRoleName = facility.facilityRoles[0].name;
            }
        }
    }

    const isUnscheduledShift = selected?.schedule_x_ShiftId === 1;
    const activeRoleName = isUnscheduledShift
        ? (unscheduledRoleName || singleUnscheduledRoleName)
        : selectedRoleName;
    const roleRequiresClient = clientRequired && !!activeRoleName && (allRolesSelected || roleMatches(activeRoleName));
   debugger;
    // A scheduled shift can carry a pre-assigned job code. When it does we show the
    // picker (pre-filled) so it auto-starts on clock-in, even if a job code isn't required.
    const shiftAssignedClientId = clientsModuleEnabled && !isUnscheduledShift ? (selected?.timecardClientId || null) : null;
    // Show the job-code picker whenever the module is on and at least one job code
    // exists. When a code isn't required it defaults to "None", letting the employee
    // start a job right at clock-in instead of via a separate Start Job step.
    const showClientPicker = roleRequiresClient || !!shiftAssignedClientId || (clientsModuleEnabled && clients.length > 0);
    const clientSelectionMissing = roleRequiresClient && !selectedClientId;
    const disableClockInButton = disableClockin || !selected.schedule_x_ShiftId;
    const disableUnscheduledClockIn = !selectedFacility || !unscheduledRoleId;

    useEffect(() => {
        if (!roleRequiresClient && clientError) {
            setClientError(false);
        }
    }, [roleRequiresClient, clientError]);

    // Prefill the job code with the one assigned to the selected shift (if any).
    // Runs whenever the clients module is on so a scheduled job auto-starts on clock-in,
    // regardless of whether a job code is strictly required.
    useEffect(() => {
        if (!clientsModuleEnabled) return;
        const shiftClientId = selected?.timecardClientId;
        if (!shiftClientId) return;
        const match = clients.find((c) => c.timecardClientId === shiftClientId);
        if (match) {
            setSelectedClientId(match.timecardClientId);
            setClientError(false);
        }
    }, [selected, clients, clientsModuleEnabled]);

    const clockInUnscheduled = () => {
        debugger;
        if (clientSelectionMissing) {
            setClientError(true);
            return;
        }
        clockIn(selectedFacility, unscheduledRoleId, null);
    }

    if (loading) {
        return <div class="loading-overlay" style={{ backgroundColor: 'var(--fui-white)' }}>
            <Spinner label="Loading" size={SpinnerSize.large} />
        </div>
    }

    return (
        <>

            {(step == "personal") &&
                <Fragment>
                    {!clockedIn &&
                        <Fragment>
                            {props.user && props.user.firstName &&
                                <div className="displayflex mb15 aligncenter mt20" css={persona}>
                                    <Persona
                                        imageInitials={props.user.firstName.substring(0, 1).toUpperCase() + props.user.lastName.substring(0, 1).toUpperCase()}
                                        imageUrl={process.env.REACT_APP_CDN_URL + props.user.userImg}
                                        initialsColor={props.user.avatarColor}
                                        size={PersonaSize.size32}
                                    />
                                    <h5 className="head-xs ">Hi, {props.user.firstName}</h5>
                                </div>
                            }

                            <h3 className="head-l mb10" style={{ fontSize: 24, fontWeight: 300 }}>Would you like to clock in?</h3>
                            {availableShifts.map(x => button(x, (selected.schedule_x_ShiftId == x.schedule_x_ShiftId)))}
                            {button({ schedule_x_ShiftId: 1 }, (selected.schedule_x_ShiftId == 1))}
                            {availableShifts && availableShifts.length == 0 && <h5 className="head-xs mb10">No shifts today.</h5>}

                            {showClientPicker && !selectFacility && !selectRole &&
                                <>
                                    <div style={{ width: '80%', height: 1, backgroundColor: '#dfe4e8', margin: '12px auto' }} />
                                    <div className="mb15 mt10 flexcolumn justifycenter aligncenter">
                                        <h3 className="head-l mb5" style={{ fontSize: 20, fontWeight: 400 }}>{roleRequiresClient ? 'What job code are you working on?' : 'Starting a job?'}</h3>
                                        <Dropdown
                                            selectedKey={selectedClientId}
                                            onChange={(e, option) => {
                                                setSelectedClientId(option ? option.key : null);
                                                setClientError(false);
                                            }}
                                            placeholder={loadingClients ? "Loading job codes..." : "Select a job code"}
                                            disabled={loadingClients || !clients.length}
                                            className="mb5"
                                            options={[
                                                ...(roleRequiresClient ? [] : [{ key: null, text: 'None' }]),
                                                ...clients.map((client) => ({ key: client.timecardClientId, text: client.name }))
                                            ]}
                                            calloutProps={{
                                                styles: {
                                                    root: {
                                                        border: '1px solid rgba(var(--palette-neutral-100), 0.18)',
                                                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2.4px 5.4px 0px, rgba(0, 0, 0, 0.0807) 0px 1.2px 1.6px 0px',
                                                        borderRadius: 5, overflow: 'hidden'
                                                    },
                                                },
                                            }}
                                            styles={{
                                                root: { width: 280 },
                                                title: { height: '45px', lineHeight: '45px', borderRadius: 4, borderColor: 'rgba(0, 0, 0, 0.21)' },
                                                caretDown: { lineHeight: '45px', fontWeight: '600' },
                                            }}
                                            errorMessage={clientError ? 'A job code is required to clock in.' : undefined}
                                        />
                                        {!loadingClients && !clients.length &&
                                            <span className="secondary-medium">No job codes are available. Ask a manager to add one.</span>
                                        }
                                    </div>
                                </>
                            }

                            {lateError && <h5 className="head-xs mb10" style={{ color: 'red' }}>Your late for your shift!</h5>}
                            {countdownDate && <h5 className="head-xs mb10">Clock in enabled in: <Countdown date={countdownDate} renderer={renderer} /></h5>}
                            <div className="displayflex width100 mt15 justifycenter">
                                <PrimaryButton style={{ marginRight: 3,fontSize: 16, height: 40, width: 120, }} disabled={disableClockInButton} onClick={(e) => tryClockIn()}>{inFlight === 'clockIn' ? <Spinner size={SpinnerSize.medium} /> : 'Clock In'}</PrimaryButton>
                                <DefaultButton style={{ marginLeft: 3,fontSize: 16, height: 40, width: 120, }} onClick={props.close} text="Not Yet" />
                            </div>
                            {(selectFacility || selectRole) &&
                                <div className="pabsolute white-to-grey flexcolumn aligncenter justifycenter" style={{ inset: 0, borderRadius: 8 }}>
                                    {selectFacility && !selectRole && <>
                                        <h3 className="head-l mb20" style={{ fontSize: 20, fontWeight: 400 }}>What location are you clocking into?</h3>
                                        <Dropdown
                                            selectedKey={selectedFacility}
                                            onChange={(e, i) => {

                                                setSelectedFacility(i.key)

                                                if (localUserFacilities.filter(x => x.facilityID == i.key)[0].facilityRoles.length > 1) {
                                                    setSelectRole(true)
                                                } else {
                                                    setUnscheduledRoleId(localUserFacilities.filter(x => x.facilityID == i.key)[0].facilityRoles[0].roleId)
                                                }
                                            }}
                                            placeholder="Select location"
                                            className="mb10"
                                            options={localUserFacilities.map(x => ({ key: x.facilityID, text: x.name }))}
                                            calloutProps={{
                                                styles: {
                                                    root: {
                                                        border: '1px solid rgba(var(--palette-neutral-100), 0.18)',
                                                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2.4px 5.4px 0px, rgba(0, 0, 0, 0.0807) 0px 1.2px 1.6px 0px',
                                                        borderRadius: 5, overflow: 'hidden'
                                                    },
                                                },
                                            }}
                                            styles={{
                                                root: { width: 270, },
                                                title: { height: '45px', lineHeight: '45px', borderRadius: 4, borderColor: 'rgba(0, 0, 0, 0.21)' },
                                                caretDown: { lineHeight: '45px', fontWeight: '600' },
                                            }}
                                        />
                                    </>}
                                    {selectRole && !roleRequiresClient && <>
                                        <h3 className="head-l mb20" style={{ fontSize: 20, fontWeight: 400 }}>What role are you clocking into?</h3>
                                        <Dropdown
                                            selectedKey={unscheduledRoleId}
                                            onChange={(e, i) => setUnscheduledRoleId(i.key)}
                                            placeholder="Select Role"
                                            className="mb10"
                                            options={localUserFacilities.filter(x => x.facilityID == selectedFacility)[0].facilityRoles.map(x => ({ key: x.roleId, text: x.name }))}
                                            calloutProps={{
                                                styles: {
                                                    root: {
                                                        border: '1px solid rgba(var(--palette-neutral-100), 0.18)',
                                                        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2.4px 5.4px 0px, rgba(0, 0, 0, 0.0807) 0px 1.2px 1.6px 0px',
                                                        borderRadius: 5, overflow: 'hidden'
                                                    },
                                                },
                                            }}
                                            styles={{
                                                root: { width: 270, },
                                                title: { height: '45px', lineHeight: '45px', borderRadius: 4, borderColor: 'rgba(0, 0, 0, 0.21)' },
                                                caretDown: { lineHeight: '45px', fontWeight: '600' },
                                            }}
                                        />
                                    </>}
                                    {(clientsModuleEnabled && clients.length > 0) &&
                                        <div className="mt10 flexcolumn aligncenter">
                                            <h3 className="head-l mb5" style={{ fontSize: 20, fontWeight: 400 }}>{roleRequiresClient ? 'What job code are you working on?' : 'Starting a job?'}</h3>
                                            <Dropdown
                                                selectedKey={selectedClientId}
                                                onChange={(e, option) => {
                                                    setSelectedClientId(option ? option.key : null);
                                                    setClientError(false);
                                                }}
                                                placeholder={loadingClients ? "Loading job codes..." : "Select a job code"}
                                                disabled={loadingClients || !clients.length}
                                                className="mb5"
                                                options={[
                                                    ...(roleRequiresClient ? [] : [{ key: null, text: 'None' }]),
                                                    ...clients.map((client) => ({ key: client.timecardClientId, text: client.name }))
                                                ]}
                                                calloutProps={{
                                                    styles: {
                                                        root: {
                                                            border: '1px solid rgba(var(--palette-neutral-100), 0.18)',
                                                            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2.4px 5.4px 0px, rgba(0, 0, 0, 0.0807) 0px 1.2px 1.6px 0px',
                                                            borderRadius: 5, overflow: 'hidden'
                                                        },
                                                    },
                                                }}
                                                styles={{
                                                    root: { width: 270 },
                                                    title: { height: '45px', lineHeight: '45px', borderRadius: 4, borderColor: 'rgba(0, 0, 0, 0.21)' },
                                                    caretDown: { lineHeight: '45px', fontWeight: '600' },
                                                }}
                                                errorMessage={clientError ? 'A job code is required to clock in.' : undefined}
                                            />
                                        </div>
                                    }
                                    <PrimaryButton className="mt20" disabled={disableUnscheduledClockIn} onClick={() => clockInUnscheduled()} style={{ width: 200, height: 40 }}>{inFlight === 'clockIn' ? <Spinner size={SpinnerSize.medium} /> : 'Clock In'}</PrimaryButton>

                                </div>
                            }

                        </Fragment>
                    }
                    {clockedIn &&
                        <Fragment>
                            {!onBreak &&
                                <Fragment>
                                    {props.user && props.user.firstName &&
                                        <div className="displayflex mb15 aligncenter mt20" css={persona}>
                                            <Persona
                                                imageInitials={props.user.firstName.substring(0, 1).toUpperCase() + props.user.lastName.substring(0, 1).toUpperCase()}
                                                imageUrl={process.env.REACT_APP_CDN_URL + props.user.userImg}
                                                initialsColor={props.user.avatarColor}
                                                size={PersonaSize.size32}
                                            />
                                            <h5 className="head-xs ">Hi, {props.user.firstName}</h5>
                                        </div>
                                    }
                                    {!chooseBreakType &&
                                        <>
                                            <h3 className="head-l mb20" style={{ fontSize: 28, fontWeight: 300 }}>You clocked in at</h3>

                                            <div className="tcenter displayflex justifycenter mb15" style={{ width: 190, fontSize: 42, lineHeight: '32px', alignItems: 'baseline' }}>
                                                {moment(clockedInData.date).format('h:mm')}<div style={{ fontSize: 13, fontWeight: '400', marginLeft: 5 }}>{moment(clockedInData.date).format(' A')}</div>
                                            </div>

                                            <div className="displayflex justifycenter aligncenter">
                                                {props.data.breakTypeId == 1 && <PrimaryButton className="mt10" style={{ fontSize: 16, height: 44, width: 140, marginRight: 3 }} onClick={() => setChooseBreakType(true)} text="Take a break" />}
                                                <DefaultButton className="mt10" style={{ fontSize: 16, height: 44, width: 140, marginLeft: 3 }} onClick={() => clockOut()}>{inFlight === 'clockOut' ? <Spinner size={SpinnerSize.medium} /> : 'Clock Out'}</DefaultButton>
                                            </div>
                                            {clientsModuleEnabled && (() => {
                                                const activeJob = (clockedInData?.jobSegments || []).find(s => !s.endTime);
                                                return (
                                                    <div className="mt20 width100 flexcolumn aligncenter">
                                                        <div style={{ width: '80%', height: 1, backgroundColor: '#dfe4e8', margin: '4px auto 14px' }} />
                                                        {activeJob
                                                            ? <>
                                                                <span className="secondary-medium mb5">Current job code</span>
                                                                <h3 className="head-l mb10" style={{ fontSize: 20, fontWeight: 400 }}>{activeJob.clientName}</h3>
                                                                {!showJobPicker &&
                                                                    <div className="displayflex">
                                                                        <PrimaryButton style={{ fontSize: 15, height: 40, width: 130, marginRight: 3 }} onClick={() => endJob()}>{inFlight === 'endJob' ? <Spinner size={SpinnerSize.medium} /> : 'End Job'}</PrimaryButton>
                                                                        <DefaultButton style={{ fontSize: 15, height: 40, width: 130, marginLeft: 3 }} onClick={() => { setJobPickerClientId(null); setShowJobPicker(true); }} text="Change Job" />
                                                                    </div>
                                                                }
                                                            </>
                                                            : <>
                                                                {!showJobPicker &&
                                                                    <PrimaryButton style={{ fontSize: 15, height: 40, width: 200 }} onClick={() => { setJobPickerClientId(null); setShowJobPicker(true); }} text="Start Job" />
                                                                }
                                                            </>
                                                        }
                                                        {showJobPicker &&
                                                            <div className="flexcolumn aligncenter mt5">
                                                                <h3 className="head-l mb10" style={{ fontSize: 18, fontWeight: 400 }}>What job code are you working on?</h3>
                                                                <Dropdown
                                                                    selectedKey={jobPickerClientId}
                                                                    onChange={(e, option) => setJobPickerClientId(option ? option.key : null)}
                                                                    placeholder={loadingClients ? "Loading job codes..." : "Select a job code"}
                                                                    disabled={loadingClients || !clients.length || jobBusy}
                                                                    className="mb5"
                                                                    options={clients.map((client) => ({ key: client.timecardClientId, text: client.name }))}
                                                                    calloutProps={{
                                                                        styles: {
                                                                            root: {
                                                                                border: '1px solid rgba(var(--palette-neutral-100), 0.18)',
                                                                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2.4px 5.4px 0px, rgba(0, 0, 0, 0.0807) 0px 1.2px 1.6px 0px',
                                                                                borderRadius: 5, overflow: 'hidden'
                                                                            },
                                                                        },
                                                                    }}
                                                                    styles={{
                                                                        root: { width: 270 },
                                                                        title: { height: '45px', lineHeight: '45px', borderRadius: 4, borderColor: 'rgba(0, 0, 0, 0.21)' },
                                                                        caretDown: { lineHeight: '45px', fontWeight: '600' },
                                                                    }}
                                                                />
                                                                <div className="displayflex mt10">
                                                                    <PrimaryButton style={{ fontSize: 15, height: 40, width: 130, marginRight: 3 }} disabled={!jobPickerClientId} onClick={() => startJob(jobPickerClientId)}>{inFlight === 'startJob' ? <Spinner size={SpinnerSize.medium} /> : (activeJob ? "Change Job" : "Start Job")}</PrimaryButton>
                                                                    <DefaultButton style={{ fontSize: 15, height: 40, width: 130, marginLeft: 3 }} onClick={() => { setShowJobPicker(false); setJobPickerClientId(null); }} text="Cancel" />
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    }
                                    {chooseBreakType &&
                                        <>
                                            <div className="mt5">
                                                {props.data.breaks.map(x => {
                                                    return <div>
                                                        <CompoundButton css={buttonCss} onClick={() => setClientBreakId(x.clientBreakId)} className={x.clientBreakId == clientBreakId ? "active mt15" : " mt15"} primary={x.clientBreakId == clientBreakId}>
                                                            <Clock20Regular />
                                                            Take a {x.minutes} <span className="mx5" style={{ fontWeight: '500' }}>{x.paid ? 'Paid' : 'Unpaid'}</span> break
                                                        </CompoundButton>
                                                    </div>
                                                })}
                                                <div className="displayflex mt15" style={{ width: 280 }}>
                                                    <PrimaryButton style={{ marginRight: 3, fontSize: 16, height: 44, width: 140 }} className="mt10" disabled={!clientBreakId} onClick={() => takeABreak(clientBreakId)}>{inFlight === 'takeABreak' ? <Spinner size={SpinnerSize.medium} /> : 'Take a break'}</PrimaryButton>
                                                    <DefaultButton style={{ marginLeft: 3, fontSize: 16, height: 44, width: 140 }} className="mt10" onClick={() => { setChooseBreakType(false); setClientBreakId(null) }} text="Cancel" />
                                                </div>
                                            </div>
                                        </>
                                    }
                                </Fragment>
                            }
                            {onBreak &&
                                <Fragment>
                                    <i style={{ fontSize: 50, marginTop: 15 }} className="ms-Icon ms-Icon--CoffeeScript" aria-hidden="true"></i>
                                    <h3 className="head-l " style={{ fontSize: 28, fontWeight: 300 }}>Your on a break</h3>
                                    {/* CHANGED: show the live string, not moment(...).format(...) */}
                                    <p className="head-m tcenter displayflex justifycenter my10" style={{ width: 190, fontSize: 24, lineHeight: '32px', fontWeight: '400' }}>{breakDuration}</p>
                                    <div>
                                        <PrimaryButton style={{ fontSize: 16, height: 44, width: 160 }} className="mt10" onClick={() => endBreak()}>{inFlight === 'endBreak' ? <Spinner size={SpinnerSize.medium} /> : 'End Break'}</PrimaryButton>
                                    </div>
                                </Fragment>
                            }
                        </Fragment>
                    }

                </Fragment>
            }
            {(step == "clockOutSuccess") &&
                <div style={{ width: 200 }} className="flexcolumn justifycenter aligncenter displayflex">
                    <Lottie
                        options={{
                            animationData: animation2,
                            loop: false,
                        }}
                    />

                    <p className="secondary-medium tcenter mb5 mt5">You successfully clocked out.</p>
                    <h3 className="head-l" style={{ fontSize: 24 }}>Enjoy your day!</h3>
                </div>
            }
            {(step == "success" && !selected.note) &&
                <div style={{ width: 200 }} className="flexcolumn justifycenter aligncenter displayflex">
                    <Lottie
                        options={{
                            animationData: animation,
                            loop: false,
                        }}
                    />
                    <h3 className="head-l" style={{ fontSize: 24 }}>Enjoy your shift!</h3>
                    <p className="secondary-medium tcenter mb5 mt5">You clocked in at.</p>
                    <div style={{ fontSize: 24 }}>{moment().format('h:mm A')}</div>
                </div>
            }
            {(step == "success" && selected.note) &&
                <div style={{ height: 200 }} className="width100">
                    <h3 className="head-l mb20" style={{ fontSize: 24, fontWeight: 500 }}>Shift notes:</h3>
                    <span style={{ fontSize: 18 }}>{htmr(selected.note)}</span>
                </div>
            }

            {(step == "tips") &&
                <div style={{ height: 200 }} className="width100 displayflex">
                    <form onSubmit={handleSubmit} className=" justifycenter flexcolumn">
                        <h3 className="head-l mb20" style={{ fontSize: 20, fontWeight: 400 }}>Would you like to add your cash tips?</h3>
                        <div className="displayflex aligncenter" style={{ fontSize: 19 }}>
                            <TextField
                                value={tips}
                                prefix="$"
                                onChange={(e) => setTips(e.target.value)}
                                className="spin-button"
                                styles={{ fieldGroup: { marginLeft: 2, height: '34px', width: 140 }, field: { height: '32px' } }}
                                onKeyPress={(event) => {
                                    if (!/[0-9.]/.test(event.key) || (event.key === '.' && event.target.value.includes('.'))) {
                                        event.preventDefault();
                                    }
                                }}
                            />
                        </div>
                        <div className="displayflex mt15" style={{ width: 280 }}>
                            <DefaultButton style={{ marginRight: 3, fontSize: 16, height: 36, width: 100 }} className="mt10" onClick={() => showClockoutSuccess(true)} text="Later" />
                            <PrimaryButton style={{ marginLeft: 3, fontSize: 16, height: 36, width: 100 }} disabled={!tips} className="mt10" type="submit">{inFlight === 'tips' ? <Spinner size={SpinnerSize.medium} /> : 'Add'}</PrimaryButton>
                        </div>
                    </form>
                </div>
            }
            <Dialog
                hidden={!showAlert}
                onDismiss={() => setShowAlert(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: alertTitle,
                    subText: alertMessage,
                }}
                modalProps={{
                    isBlocking: false,
                    styles: { main: { maxWidth: 450 } },
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={() => setShowAlert(false)} text="OK" />
                </DialogFooter>
            </Dialog>

        </>
    );
}

export default ClockinForm;
